import { type CustomerAgeingData, type AgeingMetrics, type SmartActionsRecommendation, type SmartAction, type TransactionAgeingData, type CustomerActivity, type PaymentHistoryItem, type OutstandingInvoice, type CustomerMetrics } from "@/types/ageing-analysis";

const companyNames = [
  "Acme Corporation",
  "Global Tech Solutions",
  "Premier Manufacturing Ltd",
  "Sunrise Retail Group",
  "Metro Construction Co",
  "Apex Logistics Inc",
  "Horizon Healthcare",
  "Summit Financial Services",
  "Pinnacle Automotive",
  "Vertex Energy Systems",
  "Nexus Communications",
  "Quantum Industries",
  "Stellar Pharmaceuticals",
  "Atlas Distribution",
  "Fusion Technologies",
  "Cascade Hospitality Group",
  "Meridian Consulting",
  "Titan Steel Works",
  "Zenith Marketing Agency",
  "Omega Engineering",
  "Pacific Trading Company",
  "Vanguard Security Services",
  "Prestige Property Management",
  "Infinity Software Solutions",
  "Cornerstone Building Supplies",
  "Velocity Transport Ltd",
  "Silverline Electronics",
  "Paramount Food Services",
  "Evergreen Landscaping",
  "Benchmark Industrial Equipment"
];

const creditControllers = [
  "Sarah Johnson",
  "Mike Williams",
  "Emma Davis",
  "James Wilson",
  "Lucy Thompson",
];

const accountManagers = [
  "Tom Harris",
  "Rachel Green",
  "David Brown",
  "Claire Foster",
  "Mark Spencer",
];

const userSortKeyValues = ["Alpha", "Beta", "Gamma", "Delta", "Epsilon", "Zeta", "Eta", "Theta"];

const collectionStrategies = [
  "Standard Collection Flow",
  "Aggressive Collection",
  "Gentle Reminder Flow",
  "High-Value Customer",
  "Legal Action Track"
];

const paymentTerms = [
  "Net 30",
  "Net 60",
  "Net 90",
  "COD",
  "Net 15",
  "Net 45",
  "2/10 Net 30",
  "Due on Receipt"
];

function randomBetween(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomFloat(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

function generateAgeingDistribution(totalOutstanding: number, riskLevel: string) {
  const distribution = {
    current: 0,
    days31_60: 0,
    days61_90: 0,
    days91_120: 0,
    over120: 0
  };

  // Risk-based distribution
  if (riskLevel === "critical") {
    // Critical: Heavy concentration in old buckets
    distribution.over120 = totalOutstanding * randomFloat(0.4, 0.6);
    distribution.days91_120 = totalOutstanding * randomFloat(0.15, 0.25);
    distribution.days61_90 = totalOutstanding * randomFloat(0.1, 0.15);
    distribution.days31_60 = totalOutstanding * randomFloat(0.05, 0.1);
    distribution.current = totalOutstanding - (distribution.over120 + distribution.days91_120 + distribution.days61_90 + distribution.days31_60);
  } else if (riskLevel === "high") {
    // High: Moderate concentration in old buckets
    distribution.over120 = totalOutstanding * randomFloat(0.2, 0.35);
    distribution.days91_120 = totalOutstanding * randomFloat(0.15, 0.25);
    distribution.days61_90 = totalOutstanding * randomFloat(0.15, 0.2);
    distribution.days31_60 = totalOutstanding * randomFloat(0.1, 0.15);
    distribution.current = totalOutstanding - (distribution.over120 + distribution.days91_120 + distribution.days61_90 + distribution.days31_60);
  } else if (riskLevel === "medium") {
    // Medium: Balanced distribution
    distribution.over120 = totalOutstanding * randomFloat(0.05, 0.15);
    distribution.days91_120 = totalOutstanding * randomFloat(0.1, 0.2);
    distribution.days61_90 = totalOutstanding * randomFloat(0.15, 0.25);
    distribution.days31_60 = totalOutstanding * randomFloat(0.2, 0.3);
    distribution.current = totalOutstanding - (distribution.over120 + distribution.days91_120 + distribution.days61_90 + distribution.days31_60);
  } else {
    // Low: Most in current
    distribution.current = totalOutstanding * randomFloat(0.5, 0.7);
    distribution.days31_60 = totalOutstanding * randomFloat(0.15, 0.25);
    distribution.days61_90 = totalOutstanding * randomFloat(0.05, 0.15);
    distribution.days91_120 = totalOutstanding * randomFloat(0.02, 0.08);
    distribution.over120 = totalOutstanding - (distribution.current + distribution.days31_60 + distribution.days61_90 + distribution.days91_120);
  }

  return distribution;
}

function calculateWorstDaysOverdue(ageing: { current: number; days31_60: number; days61_90: number; days91_120: number; over120: number }): number {
  if (ageing.over120 > 0) return randomBetween(121, 180);
  if (ageing.days91_120 > 0) return randomBetween(91, 120);
  if (ageing.days61_90 > 0) return randomBetween(61, 90);
  if (ageing.days31_60 > 0) return randomBetween(31, 60);
  return randomBetween(0, 30);
}

function calculateAIPriorityScore(customer: Partial<CustomerAgeingData>): number {
  let score = 0;
  
  // Risk level weight (40 points)
  if (customer.riskLevel === "critical") score += 40;
  else if (customer.riskLevel === "high") score += 30;
  else if (customer.riskLevel === "medium") score += 20;
  else score += 10;
  
  // Days overdue weight (30 points)
  const worstDays = customer.worstDaysOverdue || 0;
  if (worstDays > 120) score += 30;
  else if (worstDays > 90) score += 25;
  else if (worstDays > 60) score += 20;
  else if (worstDays > 30) score += 15;
  else score += 5;
  
  // Amount weight (20 points)
  const amount = customer.totalOutstanding || 0;
  if (amount > 200000) score += 20;
  else if (amount > 100000) score += 15;
  else if (amount > 50000) score += 10;
  else score += 5;
  
  // Contact recency weight (10 points)
  const lastContact = new Date(customer.lastContactDate || new Date());
  const daysSinceContact = Math.floor((Date.now() - lastContact.getTime()) / (1000 * 60 * 60 * 24));
  if (daysSinceContact > 60) score += 10;
  else if (daysSinceContact > 30) score += 7;
  else if (daysSinceContact > 14) score += 5;
  else score += 2;
  
  return Math.min(100, score);
}

function generateRecentActivity(customerId: string, riskLevel: string): CustomerActivity[] {
  const activityTypes: CustomerActivity['type'][] = ['call', 'email', 'payment', 'promise', 'note', 'dispute', 'letter', 'meeting'];
  const activities: CustomerActivity[] = [];
  const numActivities = randomBetween(3, 10);
  
  const users = ['John Smith', 'Sarah Johnson', 'Mike Williams', 'Emma Davis', 'System'];
  
  for (let i = 0; i < numActivities; i++) {
    const type = activityTypes[randomBetween(0, activityTypes.length - 1)];
    const daysAgo = randomBetween(1, 90);
    const date = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString();
    
    let summary = '';
    let outcome = '';
    let amount: number | undefined;
    
    switch (type) {
      case 'call':
        summary = 'Phone call regarding overdue payment';
        outcome = Math.random() > 0.5 ? 'Customer promised to pay within 7 days' : 'No answer, left voicemail';
        break;
      case 'email':
        summary = 'Payment reminder email sent';
        outcome = Math.random() > 0.5 ? 'Customer acknowledged receipt' : 'No response yet';
        break;
      case 'payment':
        amount = randomBetween(1000, 50000);
        summary = `Payment received - ${amount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}`;
        outcome = 'Payment successfully applied to account';
        break;
      case 'promise':
        summary = 'Promise to pay agreement created';
        outcome = `Customer committed to pay by ${new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}`;
        break;
      case 'note':
        summary = 'Internal note added to account';
        outcome = 'Customer experiencing temporary cash flow issues';
        break;
      case 'dispute':
        summary = 'Dispute raised on invoice';
        outcome = 'Investigating invoice discrepancy';
        break;
      case 'letter':
        summary = 'Formal demand letter sent';
        outcome = 'Awaiting customer response';
        break;
      case 'meeting':
        summary = 'In-person meeting with customer';
        outcome = 'Discussed payment plan options';
        break;
    }
    
    activities.push({
      id: `ACT-${customerId}-${i}`,
      type,
      date,
      summary,
      outcome: Math.random() > 0.3 ? outcome : undefined,
      user: type !== 'payment' ? users[randomBetween(0, users.length - 1)] : undefined,
      amount
    });
  }
  
  return activities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

function generatePaymentHistory(customerId: string): PaymentHistoryItem[] {
  const payments: PaymentHistoryItem[] = [];
  const numPayments = randomBetween(3, 8);
  const methods = ['Bank Transfer', 'Credit Card', 'Direct Debit', 'Check', 'Wire Transfer'];
  
  for (let i = 0; i < numPayments; i++) {
    const daysAgo = randomBetween(10, 180);
    const date = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString();
    const amount = randomBetween(5000, 100000);
    
    payments.push({
      id: `PAY-${customerId}-${i}`,
      date,
      amount,
      reference: `INV-${randomBetween(10000, 99999)}`,
      daysToPayment: randomBetween(15, 60),
      method: methods[randomBetween(0, methods.length - 1)]
    });
  }
  
  return payments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

function generateOutstandingInvoices(customerId: string, totalOutstanding: number, ageing: any): OutstandingInvoice[] {
  const invoices: OutstandingInvoice[] = [];
  const numInvoices = randomBetween(2, 8);
  let remainingAmount = totalOutstanding;
  
  const buckets = [
    { name: 'current', minDays: 0, maxDays: 30, amount: ageing.current },
    { name: 'days31_60', minDays: 31, maxDays: 60, amount: ageing.days31_60 },
    { name: 'days61_90', minDays: 61, maxDays: 90, amount: ageing.days61_90 },
    { name: 'days91_120', minDays: 91, maxDays: 120, amount: ageing.days91_120 },
    { name: 'over120', minDays: 121, maxDays: 180, amount: ageing.over120 }
  ];
  
  for (let i = 0; i < numInvoices && remainingAmount > 0; i++) {
    // Pick a random bucket with remaining amount
    const availableBuckets = buckets.filter(b => b.amount > 0);
    if (availableBuckets.length === 0) break;
    
    const bucket = availableBuckets[randomBetween(0, availableBuckets.length - 1)];
    const invoiceAmount = Math.min(
      remainingAmount,
      bucket.amount,
      randomBetween(1000, Math.max(1000, Math.floor(totalOutstanding / numInvoices)))
    );
    
    const daysOverdue = randomBetween(bucket.minDays, bucket.maxDays);
    const dueDate = new Date(Date.now() - daysOverdue * 24 * 60 * 60 * 1000);
    const invoiceDate = new Date(dueDate.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days before due date
    
    // Generate random descriptions
    const descriptions = [
      'Product delivery - Q4 2024',
      'Professional services rendered',
      'Monthly subscription fee',
      'Equipment rental charges',
      'Consulting services',
      'Software license renewal',
      'Maintenance contract',
      'Bulk order shipment',
      'Custom manufacturing order',
      'Annual service agreement'
    ];
    
    // Randomly assign flags to some invoices (30% chance for each flag)
    const hasFlags = Math.random() > 0.5;
    const flags = hasFlags ? {
      isDisputed: Math.random() > 0.8,
      hasPromiseToPay: Math.random() > 0.7,
      isInCollectionCase: Math.random() > 0.75
    } : undefined;
    
    invoices.push({
      id: `INV-${customerId}-${i}`,
      invoiceNumber: `INV-${randomBetween(10000, 99999)}`,
      date: invoiceDate.toISOString(),
      dueDate: dueDate.toISOString(),
      amount: invoiceAmount,
      outstanding: invoiceAmount,
      daysOverdue,
      ageingBucket: bucket.name,
      description: descriptions[randomBetween(0, descriptions.length - 1)],
      flags: flags
    });
    
    remainingAmount -= invoiceAmount;
    bucket.amount -= invoiceAmount;
  }
  
  return invoices.sort((a, b) => b.daysOverdue - a.daysOverdue);
}

function generateCustomerMetrics(paymentHistory: PaymentHistoryItem[], outstandingInvoices: OutstandingInvoice[]): CustomerMetrics {
  const avgPaymentDays = paymentHistory.length > 0
    ? Math.floor(paymentHistory.reduce((sum, p) => sum + (p.daysToPayment || 0), 0) / paymentHistory.length)
    : 0;
  
  const paymentReliabilityScore = Math.max(0, Math.min(100, 100 - (avgPaymentDays - 30) * 2));
  
  return {
    averagePaymentDays: avgPaymentDays,
    paymentReliabilityScore: Math.floor(paymentReliabilityScore),
    creditUtilization: 0, // Will be calculated in the component
    totalInvoicesOverdue: outstandingInvoices.filter(inv => inv.daysOverdue > 0).length
  };
}

export function generateMockCustomers(): CustomerAgeingData[] {
  const customers: CustomerAgeingData[] = [];
  
  // Risk level distribution: 10% critical, 20% high, 35% medium, 35% low
  const riskDistribution = [
    ...Array(3).fill("critical"),
    ...Array(6).fill("high"),
    ...Array(11).fill("medium"),
    ...Array(10).fill("low")
  ];
  
  for (let i = 0; i < 30; i++) {
    const riskLevel = riskDistribution[i] as "critical" | "high" | "medium" | "low";
    const totalOutstanding = randomBetween(5000, 500000);
    const creditLimit = Math.round(totalOutstanding * randomFloat(1.2, 3.0)); // Credit limit typically 1.2x to 3x of outstanding
    const ageing = generateAgeingDistribution(totalOutstanding, riskLevel);
    const worstDaysOverdue = calculateWorstDaysOverdue(ageing);
    const lastContactDate = new Date(Date.now() - randomBetween(0, 90) * 24 * 60 * 60 * 1000).toISOString();
    
    const customerId = `CUST-${String(i + 1).padStart(4, '0')}`;
    
    // Generate additional data
    const recentActivity = generateRecentActivity(customerId, riskLevel);
    const paymentHistory = generatePaymentHistory(customerId);
    const outstandingInvoices = generateOutstandingInvoices(customerId, totalOutstanding, ageing);
    const metrics = generateCustomerMetrics(paymentHistory, outstandingInvoices);
    metrics.creditUtilization = (totalOutstanding / creditLimit) * 100;
    
    const customer: Partial<CustomerAgeingData> = {
      id: customerId,
      customerName: companyNames[i],
      accountNumber: `ACC${randomBetween(10000, 99999)}`,
      creditLimit,
      currencyCreditLimit: creditLimit,
      totalOutstanding,
      ageing,
      bands: {
        1: ageing.current,
        2: ageing.days31_60,
        3: ageing.days61_90,
        4: ageing.days91_120,
        5: ageing.over120,
      },
      baseBands: {
        1: ageing.current,
        2: ageing.days31_60,
        3: ageing.days61_90,
        4: ageing.days91_120,
        5: ageing.over120,
      },
      baseTotalOutstanding: totalOutstanding,
      homeCurrencyCode: "GBP",
      averageDaysOverdue: Math.floor(worstDaysOverdue * 0.7),
      worstDaysOverdue,
      lastContactDate,
      riskLevel,
      collectionStrategy: collectionStrategies[randomBetween(0, collectionStrategies.length - 1)],
      statusFlags: {
        hasDispute: Math.random() < 0.15,
        hasPromiseToPay: Math.random() < 0.20,
        hasCollectionCase: Math.random() < 0.25,
        isOverdueAlert: worstDaysOverdue > 60,
        noRecentContact: new Date(lastContactDate).getTime() < Date.now() - 30 * 24 * 60 * 60 * 1000
      },
      currency: [
        "GBP", "GBP", "USD", "GBP", "GBP",  // 0-4
        "EUR", "GBP", "GBP", "GBP", "CAD",  // 5-9
        "GBP", "GBP", "USD", "GBP", "GBP",  // 10-14
        "GBP", "EUR", "GBP", "GBP", "GBP",  // 15-19
        "GBP", "CAD", "GBP", "GBP", "USD",  // 20-24
        "GBP", "GBP", "GBP", "EUR", "GBP",  // 25-29
      ][i],
      contact: `contact@${companyNames[i].toLowerCase().replace(/\s+/g, '')}.com`,
      phone: `+44 ${randomBetween(100, 999)} ${randomBetween(1000, 9999)} ${randomBetween(1000, 9999)}`,
      lastPayment: new Date(Date.now() - randomBetween(5, 120) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      creditControllerName: Math.random() > 0.2 ? creditControllers[randomBetween(0, creditControllers.length - 1)] : undefined,
      accountManagerName: Math.random() > 0.3 ? accountManagers[randomBetween(0, accountManagers.length - 1)] : undefined,
      userSortKey1: Math.random() > 0.4 ? userSortKeyValues[randomBetween(0, userSortKeyValues.length - 1)] : undefined,
      userSortKey2: Math.random() > 0.5 ? userSortKeyValues[randomBetween(0, userSortKeyValues.length - 1)] : undefined,
      userSortKey3: Math.random() > 0.6 ? userSortKeyValues[randomBetween(0, userSortKeyValues.length - 1)] : undefined,
      userSortKey4: Math.random() > 0.7 ? userSortKeyValues[randomBetween(0, userSortKeyValues.length - 1)] : undefined,
      terms: paymentTerms[randomBetween(0, paymentTerms.length - 1)],
      recentActivity,
      paymentHistory,
      outstandingInvoices,
      metrics
    };
    
    customer.aiPriorityScore = calculateAIPriorityScore(customer);
    customers.push(customer as CustomerAgeingData);
  }
  
  // Sort by AI priority score descending
  return customers.sort((a, b) => b.aiPriorityScore - a.aiPriorityScore);
}

export function generateAgeingMetrics(customers: CustomerAgeingData[]): AgeingMetrics {
  const totals = customers.reduce((acc, customer) => {
    acc.current += customer.ageing.current;
    acc.days31_60 += customer.ageing.days31_60;
    acc.days61_90 += customer.ageing.days61_90;
    acc.days91_120 += customer.ageing.days91_120;
    acc.over120 += customer.ageing.over120;
    return acc;
  }, {
    current: 0,
    days31_60: 0,
    days61_90: 0,
    days91_120: 0,
    over120: 0
  });
  
  // Generate random trends
  return {
    current: { amount: totals.current, trend: randomFloat(-15, 5) },
    days31_60: { amount: totals.days31_60, trend: randomFloat(-10, 10) },
    days61_90: { amount: totals.days61_90, trend: randomFloat(-5, 15) },
    days91_120: { amount: totals.days91_120, trend: randomFloat(-5, 20) },
    over120: { amount: totals.over120, trend: randomFloat(-10, 25) }
  };
}

export function generateSmartActions(customer: CustomerAgeingData): SmartActionsRecommendation {
  const actions: SmartAction[] = [];
  const keyInsights: string[] = [];
  const riskFactors: string[] = [];
  
  // Calculate percentages
  const over120Pct = (customer.ageing.over120 / customer.totalOutstanding) * 100;
  const over90Pct = ((customer.ageing.over120 + customer.ageing.days91_120) / customer.totalOutstanding) * 100;
  const over60Pct = ((customer.ageing.over120 + customer.ageing.days91_120 + customer.ageing.days61_90) / customer.totalOutstanding) * 100;
  
  const daysSinceContact = Math.floor((Date.now() - new Date(customer.lastContactDate).getTime()) / (1000 * 60 * 60 * 24));
  
  // Generate key insights
  if (over90Pct > 50) {
    keyInsights.push(`${Math.round(over90Pct)}% of balance is critically overdue (90+ days)`);
  } else if (over60Pct > 50) {
    keyInsights.push(`${Math.round(over60Pct)}% of balance is overdue`);
  }
  
  if (daysSinceContact > 30) {
    keyInsights.push(`No contact in ${daysSinceContact} days`);
  }
  
  if (customer.statusFlags.hasDispute) {
    keyInsights.push("Active dispute requires resolution");
  }
  
  if (customer.statusFlags.hasPromiseToPay) {
    keyInsights.push("Has existing promise to pay arrangement");
  }
  
  if (customer.totalOutstanding > 100000) {
    keyInsights.push(`High-value account: ${formatCurrency(customer.totalOutstanding)}`);
  }
  
  // Generate risk factors
  if (customer.riskLevel === "critical" || customer.riskLevel === "high") {
    riskFactors.push("High credit risk profile");
  }
  
  if (over120Pct > 30) {
    riskFactors.push("Majority of debt aged over 120 days");
  }
  
  if (daysSinceContact > 45) {
    riskFactors.push("Extended period without customer contact");
  }
  
  if (customer.statusFlags.hasCollectionCase) {
    riskFactors.push("Active collection case");
  }
  
  // Generate actions based on customer profile
  
  // Critical: Legal action
  if (over120Pct > 50 && customer.riskLevel === "critical") {
    actions.push({
      id: "legal-action",
      title: "Initiate Legal Action",
      description: "Begin formal legal proceedings to recover outstanding debt",
      rationale: `With ${Math.round(over120Pct)}% of the balance aged over 120 days and a critical risk profile, legal intervention is necessary to protect the company's interests and maximize recovery potential.`,
      priority: "critical",
      category: "legal",
      estimatedImpact: {
        recoveryProbability: 65,
        timeToResolution: "60-90 days",
        riskReduction: 80
      },
      actionType: "legal",
      suggestedTiming: "Immediate",
      prerequisites: [
        "Verify all invoices and documentation are accurate",
        "Confirm customer has been notified of overdue amounts",
        "Review any existing payment arrangements or disputes"
      ],
      nextSteps: [
        "Engage legal counsel to review case",
        "Send formal demand letter",
        "File legal claim if no response within 14 days",
        "Prepare for potential court proceedings"
      ]
    });
  }
  
  // High priority: Payment plan
  if ((customer.ageing.days91_120 + customer.ageing.over120) > customer.totalOutstanding * 0.3 && !customer.statusFlags.hasPromiseToPay) {
    actions.push({
      id: "payment-plan",
      title: "Propose Structured Payment Plan",
      description: "Offer a formal payment arrangement to recover outstanding balance",
      rationale: "A structured payment plan can help the customer manage their debt while ensuring consistent recovery for your organization. This approach maintains the business relationship while addressing the overdue balance.",
      priority: customer.riskLevel === "critical" ? "critical" : "high",
      category: "payment",
      estimatedImpact: {
        recoveryProbability: 75,
        timeToResolution: "30-60 days",
        riskReduction: 60
      },
      actionType: "payment_plan",
      suggestedTiming: "Within 24 hours",
      nextSteps: [
        "Calculate affordable monthly payment amount",
        "Prepare payment plan agreement document",
        "Schedule call to discuss terms with customer",
        "Obtain signed agreement and first payment",
        "Set up automated payment reminders"
      ]
    });
  }
  
  // Immediate contact needed
  if (daysSinceContact > 30 || (customer.riskLevel === "critical" && daysSinceContact > 14)) {
    actions.push({
      id: "immediate-call",
      title: "Make Immediate Contact Call",
      description: "Urgently re-establish communication with the customer",
      rationale: `It has been ${daysSinceContact} days since last contact. Immediate communication is essential to understand the customer's situation, address any concerns, and establish a path forward for payment.`,
      priority: customer.riskLevel === "critical" ? "critical" : "high",
      category: "communication",
      estimatedImpact: {
        recoveryProbability: 55,
        timeToResolution: "7-14 days",
        riskReduction: 45
      },
      actionType: "call",
      suggestedTiming: "Immediate",
      nextSteps: [
        "Review customer account history before calling",
        "Prepare talking points and payment options",
        "Attempt contact via primary phone number",
        "If no answer, try alternate contacts",
        "Document all contact attempts and outcomes",
        "Follow up with email summarizing discussion"
      ]
    });
  }
  
  // Dispute resolution
  if (customer.statusFlags.hasDispute) {
    actions.push({
      id: "resolve-dispute",
      title: "Resolve Outstanding Dispute",
      description: "Address and close the active dispute to unblock payment",
      rationale: "The active dispute is likely preventing payment. Resolving this issue is critical to moving forward with collection efforts and maintaining the customer relationship.",
      priority: "high",
      category: "relationship",
      estimatedImpact: {
        recoveryProbability: 70,
        timeToResolution: "14-21 days",
        riskReduction: 50
      },
      actionType: "negotiate",
      suggestedTiming: "Within 48 hours",
      prerequisites: [
        "Review dispute details and supporting documentation",
        "Verify invoice accuracy and delivery confirmation",
        "Identify potential resolution options"
      ],
      nextSteps: [
        "Contact customer to discuss dispute details",
        "Investigate root cause of disagreement",
        "Propose fair resolution or compromise",
        "Document agreed resolution in writing",
        "Update account and request payment",
        "Implement process improvements to prevent future disputes"
      ]
    });
  }
  
  // Escalation warning
  if (customer.ageing.days61_90 > customer.totalOutstanding * 0.2 && customer.riskLevel !== "low") {
    actions.push({
      id: "escalation-warning",
      title: "Send Escalation Warning",
      description: "Notify customer of impending escalation to legal or collections agency",
      rationale: "A formal escalation warning can motivate payment by clearly communicating the consequences of continued non-payment, while still offering an opportunity to resolve the matter directly.",
      priority: "medium",
      category: "communication",
      estimatedImpact: {
        recoveryProbability: 60,
        timeToResolution: "14-21 days",
        riskReduction: 40
      },
      actionType: "email",
      suggestedTiming: "Within 72 hours",
      nextSteps: [
        "Draft formal escalation warning letter",
        "Include clear deadline for response (7-10 days)",
        "Outline specific consequences of non-payment",
        "Offer final opportunity for payment arrangement",
        "Send via email and certified mail",
        "Schedule follow-up call 3 days after sending"
      ]
    });
  }
  
  // Relationship review for high-value customers
  if (customer.totalOutstanding > 100000 && customer.riskLevel !== "low") {
    actions.push({
      id: "relationship-review",
      title: "Conduct Relationship Review Meeting",
      description: "Schedule senior-level discussion to address account status",
      rationale: "Given the high value of this account, a relationship review meeting can help understand underlying issues, preserve the business relationship, and develop a mutually beneficial resolution strategy.",
      priority: "medium",
      category: "relationship",
      estimatedImpact: {
        recoveryProbability: 80,
        timeToResolution: "21-30 days",
        riskReduction: 55
      },
      actionType: "visit",
      suggestedTiming: "Within 1 week",
      prerequisites: [
        "Prepare comprehensive account analysis",
        "Review customer's business situation and industry trends",
        "Identify decision-makers and key stakeholders"
      ],
      nextSteps: [
        "Request meeting with customer's finance director",
        "Prepare presentation of account status",
        "Discuss customer's challenges and constraints",
        "Explore creative payment solutions",
        "Negotiate terms that work for both parties",
        "Document agreed action plan with timelines"
      ]
    });
  }
  
  // Friendly reminder for lower risk
  if (customer.riskLevel === "low" || customer.riskLevel === "medium") {
    actions.push({
      id: "friendly-reminder",
      title: "Send Friendly Payment Reminder",
      description: "Send a courteous reminder email about outstanding balance",
      rationale: "A friendly, professional reminder can prompt payment without damaging the customer relationship. Many customers simply need a gentle nudge to prioritize payment.",
      priority: "low",
      category: "communication",
      estimatedImpact: {
        recoveryProbability: 50,
        timeToResolution: "7-14 days",
        riskReduction: 25
      },
      actionType: "email",
      suggestedTiming: "Within 24 hours",
      nextSteps: [
        "Select appropriate email template",
        "Personalize message with account details",
        "Include clear payment instructions",
        "Attach copy of outstanding invoices",
        "Send during business hours for better visibility",
        "Schedule follow-up if no response in 5 days"
      ]
    });
  }
  
  // Determine overall strategy
  let overallStrategy = "";
  if (customer.riskLevel === "critical" && over120Pct > 40) {
    overallStrategy = "Aggressive Recovery: Immediate escalation with legal action consideration";
  } else if (customer.riskLevel === "high" || over90Pct > 30) {
    overallStrategy = "Proactive Intervention: Structured payment plans and frequent contact";
  } else if (customer.riskLevel === "medium") {
    overallStrategy = "Balanced Approach: Regular reminders with relationship preservation";
  } else {
    overallStrategy = "Relationship-First: Gentle reminders maintaining positive customer relations";
  }
  
  return {
    customerId: customer.id,
    customerName: customer.customerName,
    generatedAt: new Date(),
    actions: actions.slice(0, 5), // Limit to 5 actions
    overallStrategy,
    keyInsights,
    riskFactors
  };
}

export function generateBulkSmartActions(customers: CustomerAgeingData[]): SmartActionsRecommendation {
  const totalOutstanding = customers.reduce((sum, c) => sum + c.totalOutstanding, 0);
  const avgRiskScore = customers.reduce((sum, c) => {
    const riskScore = c.riskLevel === "critical" ? 4 : c.riskLevel === "high" ? 3 : c.riskLevel === "medium" ? 2 : 1;
    return sum + riskScore;
  }, 0) / customers.length;
  
  const criticalCount = customers.filter(c => c.riskLevel === "critical").length;
  const highCount = customers.filter(c => c.riskLevel === "high").length;
  
  const keyInsights = [
    `${customers.length} customers selected`,
    `Total outstanding: ${formatCurrency(totalOutstanding)}`,
    `${criticalCount} critical risk, ${highCount} high risk`,
    `Average priority score: ${Math.round(customers.reduce((sum, c) => sum + c.aiPriorityScore, 0) / customers.length)}`
  ];
  
  const actions: SmartAction[] = [
    {
      id: "bulk-email",
      title: "Launch Bulk Email Campaign",
      description: "Send targeted reminder emails to all selected customers",
      rationale: "A coordinated email campaign allows you to efficiently communicate with multiple customers simultaneously while maintaining personalization through templates.",
      priority: "high",
      category: "communication",
      estimatedImpact: {
        recoveryProbability: 45,
        timeToResolution: "14-21 days",
        riskReduction: 30
      },
      actionType: "email",
      suggestedTiming: "Within 24 hours",
      nextSteps: [
        "Select appropriate email template for each risk level",
        "Personalize templates with customer-specific data",
        "Schedule emails for optimal delivery times",
        "Set up tracking for opens and responses",
        "Prepare follow-up sequence for non-responders"
      ]
    },
    {
      id: "call-campaign",
      title: "Organise Call Campaign",
      description: "Distribute customers among team for systematic outreach",
      rationale: "A structured call campaign ensures every customer receives personal attention while efficiently utilizing your team's resources.",
      priority: "high",
      category: "communication",
      estimatedImpact: {
        recoveryProbability: 60,
        timeToResolution: "7-14 days",
        riskReduction: 45
      },
      actionType: "call",
      suggestedTiming: "Within 48 hours",
      nextSteps: [
        "Assign customers to collection team members",
        "Provide team with customer background and talking points",
        "Set daily call targets and deadlines",
        "Implement call tracking and outcome logging",
        "Schedule daily team huddles to review progress",
        "Escalate non-responsive accounts after 3 attempts"
      ]
    },
    {
      id: "payment-plan-program",
      title: "Launch Payment Plan Program",
      description: "Offer structured payment arrangements to all selected customers",
      rationale: "A payment plan program provides customers with a manageable path to clear their debt while ensuring consistent cash flow for your organization.",
      priority: "medium",
      category: "payment",
      estimatedImpact: {
        recoveryProbability: 70,
        timeToResolution: "30-60 days",
        riskReduction: 55
      },
      actionType: "payment_plan",
      suggestedTiming: "Within 1 week",
      nextSteps: [
        "Define standard payment plan terms by risk level",
        "Create payment plan agreement templates",
        "Calculate proposed monthly payments for each customer",
        "Send payment plan offers via email and follow up with calls",
        "Set up automated payment processing and reminders",
        "Monitor plan adherence and address defaults promptly"
      ]
    }
  ];
  
  return {
    customerId: "bulk",
    customerName: `${customers.length} Selected Customers`,
    generatedAt: new Date(),
    actions,
    overallStrategy: avgRiskScore > 3 ? "Coordinated Aggressive Recovery Campaign" : avgRiskScore > 2 ? "Structured Multi-Channel Outreach" : "Systematic Reminder Campaign",
    keyInsights,
    riskFactors: []
  };
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

export function generateMockTransactions(customers: CustomerAgeingData[]): TransactionAgeingData[] {
  const transactions: TransactionAgeingData[] = [];
  const transactionTypes: Array<"Invoice" | "Credit Note" | "Payment" | "Debit Note"> = ["Invoice", "Invoice", "Invoice", "Invoice", "Credit Note", "Debit Note"];
  
  customers.forEach(customer => {
    // Generate 2-8 transactions per customer based on their outstanding amount
    const transactionCount = customer.totalOutstanding > 100000 ? randomBetween(5, 8) : randomBetween(2, 5);
    
    // Distribute the customer's ageing buckets across transactions
    const buckets: Array<{ bucket: TransactionAgeingData["ageingBucket"]; amount: number; daysRange: [number, number] }> = [
      { bucket: "current", amount: customer.ageing.current, daysRange: [0, 30] },
      { bucket: "days31_60", amount: customer.ageing.days31_60, daysRange: [31, 60] },
      { bucket: "days61_90", amount: customer.ageing.days61_90, daysRange: [61, 90] },
      { bucket: "days91_120", amount: customer.ageing.days91_120, daysRange: [91, 120] },
      { bucket: "over120", amount: customer.ageing.over120, daysRange: [121, 180] }
    ];
    
    // Filter out buckets with no amount
    const activeBuckets = buckets.filter(b => b.amount > 0);
    
    if (activeBuckets.length === 0) return;
    
    for (let i = 0; i < transactionCount; i++) {
      // Select a bucket (weighted by amount)
      const totalAmount = activeBuckets.reduce((sum, b) => sum + b.amount, 0);
      let random = Math.random() * totalAmount;
      let selectedBucket = activeBuckets[0];
      
      for (const bucket of activeBuckets) {
        random -= bucket.amount;
        if (random <= 0) {
          selectedBucket = bucket;
          break;
        }
      }
      
      const daysOverdue = randomBetween(selectedBucket.daysRange[0], selectedBucket.daysRange[1]);
      const transactionDate = new Date(Date.now() - (daysOverdue + 30) * 24 * 60 * 60 * 1000);
      const dueDate = new Date(transactionDate.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      const transactionType = transactionTypes[randomBetween(0, transactionTypes.length - 1)];
      const amount = randomBetween(1000, Math.max(2000, selectedBucket.amount / (transactionCount / activeBuckets.length)));
      const outstandingAmount = transactionType === "Invoice" || transactionType === "Debit Note" 
        ? amount * randomFloat(0.5, 1.0) 
        : 0;
      
      // Determine risk level based on days overdue
      let riskLevel: TransactionAgeingData["riskLevel"];
      if (daysOverdue > 120) riskLevel = "critical";
      else if (daysOverdue > 90) riskLevel = "high";
      else if (daysOverdue > 60) riskLevel = "medium";
      else riskLevel = "low";
      
      transactions.push({
        id: `TXN-${customer.id}-${String(i + 1).padStart(3, '0')}`,
        customerId: customer.id,
        customerName: customer.customerName,
        accountNumber: customer.accountNumber,
        referenceNumber: `INV-${randomBetween(10000, 99999)}`,
        transactionType,
        date: transactionDate.toISOString(),
        dueDate: dueDate.toISOString(),
        amount,
        outstandingAmount,
        daysOverdue,
        ageingBucket: selectedBucket.bucket,
        riskLevel,
        currency: customer.currency,
        statusFlags: {
          hasDispute: customer.statusFlags.hasDispute && Math.random() < 0.3,
          hasPromiseToPay: customer.statusFlags.hasPromiseToPay && Math.random() < 0.3,
          isOverdueAlert: daysOverdue > 60
        }
      });
    }
  });
  
  return transactions.sort((a, b) => b.daysOverdue - a.daysOverdue);
}
